import React, { useMemo } from 'react';
import { useSystem, hotkeys as hotkeysModule } from '@ohif/core';
import { UserPreferencesModal, FooterAction, Button } from '@ohif/ui-next';
import { useTranslation } from 'react-i18next';

/** Tools shown under “Measurement” in the shortcuts modal (Figma-aligned grouping). */
const MEASUREMENT_TOOL_NAMES = new Set([
  'Length',
  'Bidirectional',
  'ArrowAnnotate',
  'EllipticalROI',
  'RectangleROI',
  'CircleROI',
  'PlanarFreehandROI',
  'FreehandROI',
  'SplineROI',
  'LivewireContour',
  'CobbAngle',
  'Angle',
  'Probe',
  'UltrasoundDirectional',
  'UltrasoundBidirectional',
  'RegionArea',
  'CalRect',
  'WholeBody',
]);

interface HotkeyDefinition {
  keys: string | string[];
  label: string;
  commandName?: string;
  commandOptions?: Record<string, unknown>;
  isEditable?: boolean;
}

interface HotkeyDefinitions {
  [key: string]: HotkeyDefinition;
}

const HOTKEY_SECTION_ORDER = [
  'measurement',
  'viewport',
  'navigation',
  'windowLevel',
  'annotations',
  'editing',
  'segmentation',
  'other',
] as const;

type HotkeySectionId = (typeof HOTKEY_SECTION_ORDER)[number];

function getHotkeySection(def: HotkeyDefinition): HotkeySectionId {
  const commandName = def.commandName || '';
  const toolName = def.commandOptions?.toolName as string | undefined;

  if (commandName === 'setWindowLevelPreset') {
    return 'windowLevel';
  }
  if (commandName === 'undo' || commandName === 'redo') {
    return 'editing';
  }
  if (
    ['cancelMeasurement', 'deleteActiveAnnotation', 'acceptPreview', 'rejectPreview'].includes(
      commandName
    )
  ) {
    return 'annotations';
  }
  if (commandName === 'setToolActive') {
    if (['CircularEraser', 'CircularBrush'].includes(toolName || '')) {
      return 'segmentation';
    }
    if (toolName && MEASUREMENT_TOOL_NAMES.has(toolName)) {
      return 'measurement';
    }
    return 'viewport';
  }
  if (
    commandName === 'interpolateScrollForMarkerLabelmap' ||
    commandName === 'increaseBrushSize' ||
    commandName === 'decreaseBrushSize' ||
    commandName === 'addNewSegment'
  ) {
    return 'segmentation';
  }
  if (
    [
      'incrementActiveViewport',
      'decrementActiveViewport',
      'updateViewportDisplaySet',
      'nextStage',
      'previousStage',
      'nextImage',
      'previousImage',
      'firstImage',
      'lastImage',
    ].includes(commandName)
  ) {
    return 'navigation';
  }
  if (
    [
      'scaleUpViewport',
      'scaleDownViewport',
      'fitViewportToWindow',
      'rotateViewportCW',
      'rotateViewportCCW',
      'flipViewportHorizontal',
      'flipViewportVertical',
      'toggleCine',
      'invertViewport',
      'resetViewport',
    ].includes(commandName)
  ) {
    return 'viewport';
  }
  return 'other';
}

function UserPreferencesModalDefault({ hide }: { hide: () => void }) {
  const { hotkeysManager } = useSystem();
  const { t } = useTranslation('UserPreferencesModal');

  const { hotkeyDefinitions = {} } = hotkeysManager;

  const hotkeysBySection = useMemo(() => {
    const buckets = HOTKEY_SECTION_ORDER.reduce(
      (acc, id) => {
        acc[id] = [] as [string, HotkeyDefinition][];
        return acc;
      },
      {} as Record<HotkeySectionId, [string, HotkeyDefinition][]>
    );

    for (const [id, definition] of Object.entries(hotkeyDefinitions as HotkeyDefinitions)) {
      const section = getHotkeySection(definition);
      buckets[section].push([id, definition]);
    }

    for (const id of HOTKEY_SECTION_ORDER) {
      buckets[id].sort((a, b) =>
        t(a[1].label).localeCompare(t(b[1].label), undefined, { sensitivity: 'base' })
      );
    }

    return buckets;
  }, [hotkeyDefinitions, t]);

  const dismissWithoutSave = () => {
    hotkeysModule.stopRecord();
    hotkeysModule.unpause();
    hide();
  };

  return (
    <UserPreferencesModal>
      <UserPreferencesModal.HeaderBar
        title={t('Keyboard shortcuts')}
        onClose={dismissWithoutSave}
        closeLabel={t('Close', { ns: 'Common' })}
      />
      <UserPreferencesModal.Body contentClassName="gap-0 px-6 pb-6 pt-0">
        <section
          className="HotkeysPreferences flex flex-col gap-0"
          aria-label={t('Keyboard shortcuts')}
        >
          <div className="flex flex-col gap-5">
            {(() => {
              let isFirstSectionWithRows = true;
              return HOTKEY_SECTION_ORDER.map(sectionId => {
                const rows = hotkeysBySection[sectionId];
                if (!rows.length) {
                  return null;
                }
                const showSectionDivider = !isFirstSectionWithRows;
                isFirstSectionWithRows = false;
                const headingId = `user-prefs-hotkeys-${sectionId}`;
                return (
                  <div
                    key={sectionId}
                    role="group"
                    aria-labelledby={headingId}
                    className={showSectionDivider ? 'border-t border-[#EDEDED] pt-5' : 'pt-1'}
                  >
                    <h3
                      id={headingId}
                      className="mb-2.5 text-[11px] font-semibold uppercase leading-4 tracking-[0.06em] text-[#8E8E8E]"
                    >
                      {t(`HotkeysSection_${sectionId}`)}
                    </h3>
                    <UserPreferencesModal.HotkeysGrid>
                      {rows.map(([id, definition]) => (
                        <UserPreferencesModal.Hotkey
                          key={id}
                          readOnly
                          label={t(definition.label)}
                          value={definition.keys}
                        />
                      ))}
                    </UserPreferencesModal.HotkeysGrid>
                  </div>
                );
              });
            })()}
          </div>
        </section>
      </UserPreferencesModal.Body>
      <FooterAction className="border-t border-[#EDEDED] bg-white px-6 py-5 sm:px-8">
        <FooterAction.Right>
          <Button
            type="button"
            variant="outline"
            onClick={dismissWithoutSave}
            className="h-10 min-w-[88px] rounded-lg border-[#EDEDED] bg-white px-5 text-sm font-medium text-[#000000] shadow-none hover:bg-[#F5F5F5]"
          >
            {t('Close', { ns: 'Common' })}
          </Button>
        </FooterAction.Right>
      </FooterAction>
    </UserPreferencesModal>
  );
}

UserPreferencesModalDefault.title = '';
UserPreferencesModalDefault.containerClassName =
  'flex max-h-[90vh] w-full max-w-[720px] flex-col gap-0 overflow-hidden border border-[#EDEDED] bg-white p-0 shadow-lg sm:rounded-xl';

export default {
  'ohif.userPreferencesModal': UserPreferencesModalDefault,
};
